import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { LeadSellerGroupService } from '../../services/lead-seller-group/lead-seller-group.service';
import { Observable, combineLatest, Subject } from 'rxjs';
import { DealerGroup, DealerGroupMember, DealerGroupsState } from '../../interfaces/dealer-group.interface';
import {
  selectGroup,
  selectGroupIsSelected,
  selectGroupMembers,
  selectGroupMemberSelectedCount
} from '../../services/lead-seller-group/reducer';
import { map, takeUntil } from 'rxjs/operators';
import { LeadSellerPreferencesService } from '../../services/lead-seller-preferences/lead-seller-preferences.service';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  SearchFilterSectionComponent
} from '../search-filter/search-filter-section/search-filter-section.component';
import { TrackDirective } from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';


@Component({
    selector: 'app-dealer-group-section',
    templateUrl: './dealer-group-section.component.html',
    styleUrls: ['./dealer-group-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslocoDirective, SearchFilterSectionComponent, TrackDirective, CommonModule]
})
export class DealerGroupSectionComponent implements OnInit, OnDestroy {
  @Input() state$: Observable<DealerGroupsState>;
  @Output() selectGroup = new EventEmitter<string>();
  @Output() selectMember = new EventEmitter<string>();

  @Input() groupUuid: string;
  @Input() dealershipGroupCount: number;
  @Input() leadSellerPreferencePopOver: boolean;
  destroyed$ = new Subject();
  group$: Observable<DealerGroup>;
  members$: Observable<DealerGroupMember[]>;
  selectedMemberCount$: Observable<number>;
  isGroupSelected$: Observable<boolean>;
  searchTerm$: Observable<string>;
  filteredMembers$: Observable<DealerGroupMember[]>;
  open = false;
  tagName = 'lead-seller';

  constructor(
    public readonly leadSellerGroupService: LeadSellerGroupService,
    public leadSellerPreferencesService: LeadSellerPreferencesService
  ) {}

  ngOnInit() {
    this.searchTerm$ = this.leadSellerGroupService.searchTerm$;

    this.group$ = this.state$.pipe(map(selectGroup(this.groupUuid)));
    this.members$ = this.state$.pipe(map(selectGroupMembers(this.groupUuid)));
    this.filteredMembers$ = combineLatest(this.members$, this.searchTerm$).pipe(
      map(([members, searchTerm]) => members.filter(member => member.oeName.toLowerCase().includes(searchTerm)))
    );
    combineLatest(this.filteredMembers$, this.searchTerm$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([filteredMembers, searchTerm]) => {
        if (searchTerm !== '') {
          this.open = filteredMembers.length > 0;
        }
      });
    this.selectedMemberCount$ = this.state$.pipe(map(selectGroupMemberSelectedCount(this.groupUuid)));
    this.isGroupSelected$ = this.state$.pipe(map(selectGroupIsSelected(this.groupUuid)));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  toggleGroup() {
    this.selectGroup.emit(this.groupUuid);
  }

  toggleMember(uuid: string) {
    this.selectMember.emit(uuid);
  }

  toggleAllMembersInGroup() {
    if (this.leadSellerPreferencePopOver) {
      this.leadSellerPreferencesService.toggleAll();
    } else {
      this.leadSellerGroupService.toggleAll();
    }
  }

  get isSellerAssociatedToMultipleLeadSellerGroup() {
    return this.dealershipGroupCount && this.dealershipGroupCount > 1;
  }

  get isSellerAssociatedToSingleLeadSellerGroup() {
    return this.dealershipGroupCount && this.dealershipGroupCount === 1;
  }
}
